import { Flex, Text } from "@chakra-ui/react";
import { PriceAndStrikePriceLabel } from "./PriceAndStrikePriceLabel";
import { Bundle, Price } from "../../../types";
import { useAccountData } from "../../../hooks/useAccountData";

export interface BundleCardInformationSectionProps {
  content: Bundle;
  priceToUse?: Price;
  discountPercentage?: number;
  showName?: boolean;
  showReleasesPages?: boolean;
  showDescription?: boolean;
  nameNoOfLines?: number;
  namePaddingTop?: string;
  showPrice?: boolean;
}

export function BundleCardInformationSection({
  content,
  priceToUse,
  showName = true,
  showReleasesPages = true,
  showDescription = false,
  nameNoOfLines = 1,
  namePaddingTop,
  showPrice = true,
}: BundleCardInformationSectionProps) {
  const totalReleases = content.total_items;
  const totalPages = content.total_pages;

  return (
    <Flex direction="column" gap={1}>
      {/* Name */}
      {showName && (
        <Text
          variant="cardHeader"
          pt={namePaddingTop}
          noOfLines={nameNoOfLines}
          title={content.display_name}
        >
          {content.display_name}
        </Text>
      )}
      {/* Releases + Pages */}
      {showReleasesPages && (
        <Text fontSize="12px" fontWeight="400" color="neutral.200">
          {totalReleases} release
          {totalReleases > 1 ? "s" : ""} • {totalPages} pages
        </Text>
      )}
      {/* Description */}
      {showDescription && content.description && (
        <Text
          fontSize="14px"
          fontWeight="400"
          color="neutral.200"
          noOfLines={2}
          className="multiline-clamp"
          title={content.description}
        >
          {content.description}
        </Text>
      )}
      {/* Prices */}
      {showPrice && <BundlePriceLabel content={content} priceToUse={priceToUse} />}
    </Flex>
  );
}

/**
 * Picks the right price treatment for a bundle card. Claimable Gold-Monthly
 * bundles (Gold user, bundle is the active monthly reward, not yet claimed)
 * show "$0.00" + strikethrough retail to mirror the release pricing on the
 * same surfaces. Everything else falls through to the existing
 * discounted-or-regular display.
 */
function BundlePriceLabel({
  content,
  priceToUse,
}: {
  content: Bundle;
  priceToUse?: Price;
}) {
  const accountData = useAccountData();
  const userIsGold = !!accountData?.user?.gold;

  const isClaimable =
    userIsGold &&
    !!content.gold_monthly_title_id &&
    content.gold_monthly_claim_status === "unclaimed";

  if (isClaimable) {
    return (
      <PriceAndStrikePriceLabel
        price="$0.00"
        strokePrice={content.price?.display_price}
        priceColor="success.500"
        strokeColor="neutral.400"
        fontSize="14px"
      />
    );
  }

  return (
    <PriceAndStrikePriceLabel
      price={priceToUse?.display_price_discounted}
      strokePrice={priceToUse?.display_price}
      priceColor="success.500"
      strokeColor="neutral.400"
      fontSize="14px"
    />
  );
}
